"use client";

import {
  formatPacesetterDate,
  getPacesetterAuthorizationDetails,
  getPacesetterFacility,
  getPacesetterReferralAssignee,
  getPacesetterSchedulingPriority,
  getPacesetterStickyNote,
} from "@/configuration/groups/pacesetter/routing";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { Alert, AlertDescription } from "@/src/components/shadcn/ui/alert";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { formatDateMmDdYyyy, isMinorDateOfBirth } from "@/src/lib/utils/date-and-phone-utils";
import { Referral } from "@/src/models/documents/referral";

function NotProvided() {
  return <span className="text-muted-foreground italic">Not provided</span>;
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex items-baseline gap-1">
      <span className="font-medium w-44 shrink-0">{label}:</span>
      {value ? <CopyableContent text={value} /> : <NotProvided />}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="font-semibold text-foreground mb-2">{title}</h3>
      <div className="grid grid-cols-3 gap-x-4 gap-y-2">{children}</div>
    </div>
  );
}

interface PacesetterReferralInstructionsProps {
  referral: Referral;
  faxRecipientNumber: string | null;
}

export function PacesetterReferralInstructions({ referral, faxRecipientNumber }: PacesetterReferralInstructionsProps) {
  const section = referral.section;
  const reviewedContent = (section.reviewedContent ?? {}) as Record<string, unknown>;

  const isMinor = isMinorDateOfBirth(referral.patient?.dateOfBirth);
  const stickyNote = getPacesetterStickyNote(section, faxRecipientNumber, referral.patient?.dateOfBirth ?? null);
  const assignTo = getPacesetterReferralAssignee(faxRecipientNumber);
  const facility = getPacesetterFacility(faxRecipientNumber);
  const priority = getPacesetterSchedulingPriority(reviewedContent);
  const auth = getPacesetterAuthorizationDetails(section, referral.receivingProvider);

  const dateReceived = formatPacesetterDate(referral.dateReceived);
  const dueDateDisplay = formatDateMmDdYyyy(referral.dateReceived);
  const taskDetails = dateReceived ? `${dateReceived} Referral` : "Referral";

  return (
    <div className="flex flex-col gap-8 w-full sm:min-w-[800px] max-w-[1200px] mx-auto">
      <EhrInstructionsCard>
        <div className="flex flex-col gap-6 text-sm">
          <span className="font-medium text-center">Pacesetter Referral Instructions:</span>
          <p className="text-muted-foreground">
            Reference for the ModMed task created for this referral. Click any value to copy.
          </p>
          {isMinor && (
            <Alert variant="destructive">
              <AlertDescription>
                Minor patient — verify parent/guardian info and DOB. If parent DOB is missing, enter dummy date
                01/01/1900. List parent/guardian as the policy holder. Flag the chart if parent info is missing.
              </AlertDescription>
            </Alert>
          )}

          <div>
            <h3 className="font-semibold text-foreground mb-2">Sticky Note</h3>
            <CopyableContent text={stickyNote} textClassName="whitespace-pre-wrap" />
          </div>

          <Section title="Task">
            <Field label="Assign to" value={assignTo} />
            <Field label="Task Type" value="Schedule" />
            <Field label="Priority" value={priority} />
            <Field label="Due date" value={dueDateDisplay} />
            <Field label="Task Details" value={taskDetails} />
            <Field label="Facility" value={facility} />
          </Section>

          <Section title="Authorization">
            <Field label="Type" value={auth.type} />
            <Field label="Authorization number" value={auth.authorizationNumber} />
            <Field label="From" value={formatDateMmDdYyyy(auth.fromDate)} />
            <Field label="To" value={formatDateMmDdYyyy(auth.toDate)} />
            <Field label="Number of visits" value={auth.numberOfVisits != null ? String(auth.numberOfVisits) : null} />
            <Field label="Description" value={auth.description} />
            <Field label="Assigned provider" value={auth.assignedProviderText} />
            <Field label="Referring provider" value={auth.referringProviderText} />
            <Field label="Referring provider NPI" value={auth.referringProviderNpi} />
          </Section>

          <Section title="Document">
            <Field label="Category" value="Referral" />
            <Field label="Label" value={taskDetails} />
          </Section>
        </div>
      </EhrInstructionsCard>

      <PatientCard patient={referral.patient} />
      <ReferralProviderCards referral={referral} />
      <ReferralCard referral={referral} isEditable />
      {referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage} type="Primary" isEditable />}
      {referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable />}
    </div>
  );
}
